import React from 'react';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import { PmsCodeModel } from '../../../../../../shared/models/organizations/PmsLookupModels';
import PmsLookupContainer, { PmsLookupState } from '../../../../../../shared/hooks/PmsLookupContainer';
import { LookupTable, RefreshButton, Toolbar, useAutoLoad } from './PmsLookupPanels';
import { DentalFormFieldVariables, isSandboxCancellationCode, isSandboxReferralTypeCode } from '../dentalSandboxWarnings';
import { SparkFormButton, StatusPill } from './sparkFormPills';
import { SparkFormBar, SparkFormRowConfirm, sameFormValue, useSparkFormField } from './sparkFormField';

interface SparkFormCodeLookupProps {
    lookup: PmsLookupState<PmsCodeModel[]>;
    refreshButton: React.ReactNode;
    // The Spark form field this code goes into, e.g. {DenticonCancelCode}
    variable: string;
    idHeader: string;
    // What the code is, for button labels, e.g. Cancellation Code
    fieldName: string;
    // Which part of the code the field stores (the ID or the code)
    valueOf: (code: PmsCodeModel) => string;
    isSandbox: (value?: string | null) => boolean;
    // All of the org's apps; the templating app is the Shared Configuration
    locationApps: ApplicationModel[];
    officeId: string;
    onFormSaved?: (appId: string, isShared: boolean) => void;
}

// PMS codes with a one-click way to put one into the Spark form, like Procedure Types
const SparkFormCodeLookup: React.FC<SparkFormCodeLookupProps> = ({ lookup, refreshButton, variable, idHeader, fieldName, valueOf, isSandbox, locationApps, officeId, onFormSaved }) => {
    const form = useSparkFormField(locationApps, officeId, variable, onFormSaved);
    const { current, target, savingValue, pending } = form;

    const renderAction = (code: PmsCodeModel) => {
        const value = valueOf(code);
        if (sameFormValue(value, current)) {
            return <StatusPill tone="done">✓ {fieldName}</StatusPill>;
        }
        if (!target) return null;
        if (pending?.key === value) return <SparkFormRowConfirm form={form} rowKey={value} />;
        return (
            <SparkFormButton type="button" onClick={() => form.request(value, `Set the ${fieldName} to ${value}${code.description ? ` (${code.description})` : ''}`)} disabled={!!savingValue || !!pending} title={`Saves ${value} to the Spark form after you confirm`}>
                {`Make ${fieldName}`}
            </SparkFormButton>
        );
    };

    return (
        <>
            <Toolbar>
                <SparkFormBar
                    form={form}
                    status={!current ? (
                        <StatusPill tone="missing">Not set</StatusPill>
                    ) : isSandbox(current) ? (
                        <StatusPill tone="warning" title="Still the Denticon sandbox value">{current} · Sandbox</StatusPill>
                    ) : (
                        <StatusPill tone="done">{current}</StatusPill>
                    )}
                />
                {refreshButton}
            </Toolbar>
            <LookupTable
                lookup={lookup}
                columns={[
                    { header: idHeader, value: row => row.id, copyable: true },
                    { header: 'Code', value: row => row.code },
                    { header: 'Description', value: row => row.description },
                    { header: 'Spark form', value: () => undefined, render: renderAction }
                ]}
            />
        </>
    );
};

interface CodeLookupProps {
    locationApps: ApplicationModel[];
    officeId: string;
    onFormSaved?: (appId: string, isShared: boolean) => void;
}

// Denticon's admin cancellation code: the form stores the code's ID
export const CancellationCodesLookup: React.FC<CodeLookupProps> = (props) => {
    const pms = PmsLookupContainer.useContainer();
    useAutoLoad(pms.cancellationCodes, () => pms.loadCancellationCodes());
    return (
        <SparkFormCodeLookup
            {...props}
            lookup={pms.cancellationCodes}
            refreshButton={<RefreshButton isLoading={pms.cancellationCodes.isLoading} onClick={() => pms.loadCancellationCodes(true)} />}
            variable={DentalFormFieldVariables.cancellationCode}
            idHeader="Cancel Code ID"
            fieldName="Cancellation Code"
            valueOf={code => code.id}
            isSandbox={isSandboxCancellationCode}
        />
    );
};

// Denticon's admin referral type: the form stores the code itself
export const ReferralTypesLookup: React.FC<CodeLookupProps> = (props) => {
    const pms = PmsLookupContainer.useContainer();
    useAutoLoad(pms.referralTypes, () => pms.loadReferralTypes());
    return (
        <SparkFormCodeLookup
            {...props}
            lookup={pms.referralTypes}
            refreshButton={<RefreshButton isLoading={pms.referralTypes.isLoading} onClick={() => pms.loadReferralTypes(true)} />}
            variable={DentalFormFieldVariables.referralTypeCode}
            idHeader="Referral Type ID"
            fieldName="Referral Type"
            valueOf={code => code.code || code.id}
            isSandbox={isSandboxReferralTypeCode}
        />
    );
};

export default SparkFormCodeLookup;
